import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React from "react";
import { HospitalityService } from "src/app/services/Index";
import "./AdminHospitality.css";

type State = {
	available: boolean;
	loading: boolean;
	saving: boolean;
	packages: any[];
	edit: any;
	fixturesFor: any;
	fixtures: any[];
	chosen: number[];
};

const money = (pence: any) => `£${((Number(pence) || 0) / 100).toFixed(2)}`;

/**
 * Hospitality packages, and which games they run at.
 *
 * The package is defined once — a club sells the same lounge every other
 * Saturday — and a separate screen says where it runs. Copying a package forty
 * times a season is how the data goes wrong, which is why the two are split.
 */
export class AdminHospitality extends React.Component<{}, State> {
	private toast: Toast | null = null;

	constructor(props: {}) {
		super(props);
		this.state = {
			available: true, chosen: [], edit: null, fixtures: [],
			fixturesFor: null, loading: true, packages: [], saving: false
		};
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const res = await HospitalityService.getPackages();
			const body = res.ok ? await res.json() : { available: false, packages: [] };
			this.setState({ available: body.available !== false, loading: false, packages: body.packages || [] });
		} catch {
			this.setState({ available: false, loading: false });
		}
	}

	blank() {
		return {
			arrivalMinutesBefore: 120, capacity: null, description: "",
			includesAdmission: true, isPublished: false, name: "", pricePence: 0, sortOrder: 0
		};
	}

	async save() {
		const { edit } = this.state;
		if (!edit?.name?.trim()) {
			this.tell("error", "The package needs a name.");
			return;
		}

		this.setState({ saving: true });
		try {
			const res = edit.id
				? await HospitalityService.updatePackage(edit.id, edit)
				: await HospitalityService.addPackage(edit);

			if (!res.ok) {
				this.tell("error", (await res.text()).replace(/^"|"$/g, "") || "Couldn't save that.");
				return;
			}
			this.setState({ edit: null });
			this.load();
		} catch {
			this.tell("error", "Couldn't save that.");
		} finally {
			this.setState({ saving: false });
		}
	}

	async remove(pkg: any) {
		// eslint-disable-next-line no-restricted-globals
		if (!window.confirm(`Stop selling ${pkg.name}? Orders that already include it are unaffected.`)) return;
		try {
			await HospitalityService.removePackage(pkg.id);
			this.load();
		} catch {
			this.tell("error", "Couldn't remove that.");
		}
	}

	async openFixtures(pkg: any) {
		try {
			const res = await HospitalityService.getFixtures(pkg.id);
			const body = res.ok ? await res.json() : { fixtures: [] };
			this.setState({
				chosen: (body.fixtures || []).filter((f: any) => f.enabled).map((f: any) => f.fixtureId),
				fixtures: body.fixtures || [],
				fixturesFor: pkg
			});
		} catch {
			this.tell("error", "Couldn't load the fixtures.");
		}
	}

	async saveFixtures(enabled: boolean) {
		const { fixturesFor, chosen, fixtures } = this.state;
		if (!fixturesFor) return;

		// Sent as one call for all of them: "on at every home game" is the
		// normal case, and forty round trips is how a club gives up on this.
		const ids = enabled ? chosen : fixtures.map((f: any) => f.fixtureId).filter((id: number) => !chosen.includes(id));
		if (!ids.length) { this.setState({ fixturesFor: null }); return; }

		this.setState({ saving: true });
		try {
			const res = await HospitalityService.setFixtures(fixturesFor.id, { enabled, fixtureIds: ids });
			if (!res.ok) {
				this.tell("error", "Couldn't save that.");
				return;
			}
		} finally {
			this.setState({ saving: false });
		}
	}

	async applyFixtures() {
		// Both directions in one go — what's ticked goes on, what isn't goes off.
		await this.saveFixtures(true);
		await this.saveFixtures(false);
		this.setState({ fixturesFor: null });
		this.tell("success", "Fixtures updated.");
		this.load();
	}

	tell(severity: "success" | "error", detail: string) {
		this.toast?.show({ closable: false, detail, life: 4000, severity, summary: "Hospitality" });
	}

	render() {
		const { available, loading, packages, edit, fixturesFor, fixtures, chosen } = this.state;

		if (!loading && !available) {
			return <p className="hp-muted">Hospitality isn&rsquo;t switched on for this club.</p>;
		}

		return (
			<div className="hp-page">
				<Toast ref={(el) => { this.toast = el; }} />

				<div className="hp-bar">
					<Button icon="pi pi-plus" label="New package" onClick={() => this.setState({ edit: this.blank() })} />
				</div>

				{loading && <p className="hp-muted">Loading…</p>}

				{!loading && !packages.length &&
					<div className="hp-empty">
						<h3>No hospitality yet</h3>
						<p>A lounge, a box, a matchday package &mdash; anything you sell alongside a ticket.</p>
					</div>}

				{packages.map((p: any) => (
					<div className="hp-card" key={p.id}>
						<div className="hp-card-head">
							<div>
								<strong>{p.name}</strong>
								{!p.isPublished && <span className="hp-tag draft">DRAFT</span>}
								{!p.includesAdmission && <span className="hp-tag warn">TICKET NOT INCLUDED</span>}
							</div>
							<span className="hp-price">{money(p.pricePence)}</span>
						</div>

						{p.description && <p className="hp-desc">{p.description}</p>}

						<div className="hp-meta">
							{p.capacity ? <span>{p.capacity} covers</span> : <span>No cap</span>}
							{p.arrivalMinutesBefore ? <span>Arrive {p.arrivalMinutesBefore} min before</span> : null}
							<span>{p.upcomingFixtures} upcoming {p.upcomingFixtures === 1 ? "game" : "games"}</span>
						</div>

						<div className="hp-actions">
							<Button className="p-button-sm p-button-text" label="Edit" onClick={() => this.setState({ edit: { ...p } })} />
							<Button className="p-button-sm p-button-text" label="Games" onClick={() => this.openFixtures(p)} />
							<Button className="p-button-sm p-button-text p-button-danger" label="Stop selling" onClick={() => this.remove(p)} />
						</div>
					</div>
				))}

				{edit &&
					<Dialog footer={<>
						<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ edit: null })} />
						<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
					</>} header={edit.id ? "Edit package" : "New package"} modal onHide={() => this.setState({ edit: null })}
						style={{ maxWidth: "95vw", width: 520 }} visible>

						<label className="hp-label">Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...edit, name: e.target.value } })}
							placeholder="Riverside Lounge" style={{ marginBottom: 14, width: "100%" }} value={edit.name || ""} />

						<label className="hp-label">What&rsquo;s included</label>
						<InputTextarea onChange={(e) => this.setState({ edit: { ...edit, description: e.target.value } })}
							rows={3} style={{ marginBottom: 14, width: "100%" }} value={edit.description || ""} />

						<label className="hp-label">Price</label>
						<InputNumber currency="GBP" locale="en-GB" mode="currency"
							onValueChange={(e) => this.setState({ edit: { ...edit, pricePence: Math.round((e.value || 0) * 100) } })}
							style={{ marginBottom: 14, width: "100%" }} value={(edit.pricePence || 0) / 100} />

						<label className="hp-label">How many covers</label>
						<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, capacity: e.value } })}
							placeholder="No limit" style={{ marginBottom: 14, width: "100%" }} value={edit.capacity} />

						{/* Asked as an offset, not a clock time — the answer is the
						    same for every fixture, and a time isn't. */}
						<label className="hp-label">Arrive how long before kick-off (minutes)</label>
						<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, arrivalMinutesBefore: e.value } })}
							style={{ marginBottom: 16, width: "100%" }} value={edit.arrivalMinutesBefore} />

						<div className="hp-switch">
							<InputSwitch checked={!!edit.includesAdmission}
								onChange={(e) => this.setState({ edit: { ...edit, includesAdmission: e.value } })} />
							<div>
								<label>Includes the match ticket</label>
								<small>Off means it&rsquo;s an upgrade &mdash; the buyer still needs a ticket to get in.</small>
							</div>
						</div>

						<div className="hp-switch">
							<InputSwitch checked={!!edit.isPublished}
								onChange={(e) => this.setState({ edit: { ...edit, isPublished: e.value } })} />
							<div>
								<label>On sale</label>
								<small>Off keeps it off the storefront while you set it up.</small>
							</div>
						</div>
					</Dialog>}

				{fixturesFor &&
					<Dialog footer={<>
						<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ fixturesFor: null })} />
						<Button disabled={this.state.saving} label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.applyFixtures()} />
					</>} header={`${fixturesFor.name} — which games`} modal onHide={() => this.setState({ fixturesFor: null })}
						style={{ maxWidth: "95vw", width: 520 }} visible>

						{!fixtures.length && <p className="hp-muted">No upcoming home games.</p>}

						{fixtures.length > 0 &&
							<Button className="p-button-sm p-button-text" label={chosen.length === fixtures.length ? "Clear all" : "Select all"}
								onClick={() => this.setState({ chosen: chosen.length === fixtures.length ? [] : fixtures.map((f: any) => f.fixtureId) })}
								style={{ marginBottom: 8 }} />}

						{fixtures.map((f: any) => (
							<label className="hp-fixture" key={f.fixtureId}>
								<input
									checked={chosen.includes(f.fixtureId)}
									onChange={(e) => this.setState({
										chosen: e.target.checked
											? [...chosen, f.fixtureId]
											: chosen.filter((id) => id !== f.fixtureId)
									})}
									type="checkbox"
								/>
								<span>{new Date(f.kickOff).toLocaleDateString("en-GB", { day: "numeric", month: "short", weekday: "short" })}</span>
							</label>
						))}
					</Dialog>}
			</div>
		);
	}
}
